import { IS_STORE_ROOT } from '@/constants';
import productHeaders from '@/constants/productHeaders';
import {
  ORDERING_OF_VIEWS, SPECIFIER_VIEW,
} from '@/constants/scheduleViews';
import router from '@/router';

import ProjectViewsApi from '@/services/graphql/projectViews';

import {
  setViewInStorage,
  getViewFromStorage,
  sortHelper,
  convertStringToArray,
} from '@/utils';

export const actions = {
  async getViews({ commit, dispatch, rootGetters, rootState, getters }, { sharedLink = null, projectId } = {
  }) {
    const { userId } = rootGetters;
    try {
      const { scheduleId } = rootState['ProjectDetailsTableSchedule'];
      const { activeWorkspaceId: workspaceId } = rootState.Workspace;

      const [_ignored, views] = await Promise.all([
        dispatch('getViewOrder', {
          scheduleId,
        }),
        ProjectViewsApi.getScheduleViews({
          scheduleId,
          projectId,
          sharedLink,
          workspaceId,
        }),
      ]);

      // Default sorting order (constant/not from server) + mapping tags + colOrder
      const customViews = views.filter(view => !ORDERING_OF_VIEWS.includes(view?.name));
      const sortedDefaultViews = ORDERING_OF_VIEWS.map(item => (views.find(i => i.name === item)));
      const sortedViews = [...sortedDefaultViews.filter(item => item), ...customViews];
      if (!sortedViews?.length) return;

      const mappedViews = sortedViews.map(view => {
        view.filterTag = filterTagFormat(view.filterTag);
        return {
          ...view,
          sortingData: sortHelper(view.id, view.sortingField),
        };
      });
      await dispatch('setViews', {
        arrViews: mappedViews,
      });

      // Setting selectedView based on saved View Order (from server)
      const firstView = getters.sortedViews?.[0];
      let selectedView = firstView || mappedViews.find(view => view.name === SPECIFIER_VIEW) || mappedViews[0];

      const { query } = router.currentRoute;
      const activeWorkspaceId = rootState.Workspace.activeWorkspaceId;
      const lastViewId = query.viewId || getViewFromStorage(userId, projectId, activeWorkspaceId);
      if (lastViewId) {
        selectedView = mappedViews.find(view => view.id === lastViewId) || selectedView;
      }
      dispatch('setSelectedViewId', selectedView);

    } catch (err) {
      console.log('getScheduleView err', err);
      commit('spinner', false, IS_STORE_ROOT);
    }
  },
  async getViewOrder({ commit }, { scheduleId }) {
    try {
      const { data } = await ProjectViewsApi.getViewOrder({
        scheduleId,
      });
      commit('setViewOrder', data.response);
    } catch (err) {
      console.error('Error getting view order', err);
    }
  },
  setViewsSorting({ state, commit }, { sortingData }) {
    const mappedViews = state.views.map(view => {
      if (view.id == sortingData.viewId) {
        const baseView = {
          ...view,
          sortingData,
        };
        return baseView;
      }
      return view;
    });
    commit('setViews', mappedViews);
  },
  setViews({ dispatch, commit, state }, { arrViews, allowSetViewMode = true, updateFilterTag = true }) {
    const updatedArrViews = arrViews.map(option => {
      // HACK - Why not migrate Sustainability View data to Power Density + change schema????
      if (option.name !== 'Sustainability View') return option;

      return {
        ...option,
        name: 'Power Density View',
      };
    });

    const toggleViewConditionsForViews = updatedArrViews.reduce((result, option) => {
      return {
        ...result,
        [option.id]: option.viewMode === 'gallery',
      };
    }, {
    });

    allowSetViewMode && dispatch('setViewMode', toggleViewConditionsForViews, IS_STORE_ROOT);
    commit('setViews', updatedArrViews);

    // If view default setting changes, active tags should update to match.
    if (!updateFilterTag) return;

    for (const view of updatedArrViews) {
      if (view.id === state.selectedViewId) {
        commit('setActiveTags', view.filterTag ?? {
        });
      }
    }
  },
  async setSelectedViewId({ commit, dispatch, rootGetters, rootState, getters }, payload) {
    if (!payload) {
      commit('setSelectedViewId', null);
      return;
    }
    commit('SearchProjectCells/clearSearchString', null, IS_STORE_ROOT);
    if (rootState.ProjectAggregation.tagTotal) {
      commit('ProjectAggregation/setTagTotal', null, IS_STORE_ROOT);
    }
    const { id: projectId } = router.currentRoute.params;
    const activeWorkspaceId = rootState.Workspace.activeWorkspaceId;
    const { userId } = rootGetters;
    commit('setSelectedViewId', payload.id);
    setViewInStorage(userId, projectId, activeWorkspaceId, payload.id);
    commit('setSelectedRowHeight', computeHeight(getters));

    const selectedView = getters.selectedView;
    if (isFilteredTagView(selectedView)) {
      await updateTagTotal({
        rootState: rootState,
        dispatch: dispatch,
        selectedView: selectedView,
      });
    }

    let activeTags = filterTagFormat(selectedView?.filterTag);
    commit('setActiveTags', activeTags);

    const { query } = router.currentRoute;
    if (query.viewId !== payload.id) {
      router
        .replace({
          query: {
            ...query,
            viewId: payload.id,
          },
        })
        .catch(e => {
          console.log(e);
        });
    }
  },
  async updateViewSettings({ commit, dispatch, rootState }, variables) {
    const { updateFilterTag = false } = variables;
    try {
      commit('spinner', true, IS_STORE_ROOT);
      const { scheduleId: tableId } = rootState['ProjectDetailsTableSchedule'];
      const { activeWorkspaceId: workspaceId } = rootState.Workspace;
      const { id: projectId } = router.currentRoute.params;
      const { data } = await ProjectViewsApi.updateViewSettings({
        ...variables,
        projectId,
        tableId,
        tableType: 'schedule',
        workspaceId,
      });
      dispatch('updateLocalViewColOrder', {
        viewData: data.response,
        updateFilterTag,
      });
    } catch (err) {
      console.log('updateViewSettings', err);
    } finally {
      commit('spinner', false, IS_STORE_ROOT);
    }
  },
  async updateViewColumnOrder({ dispatch }, { variables, updateViewData = true }) {
    try {
      const { data } = await ProjectViewsApi.updateViewColumnOrder(variables);
      if (updateViewData) {
        dispatch('updateLocalViewColOrder', {
          viewData: data.response,
          updateFilterTag: false,
        });
      }
    } catch (err) {
      dispatch('handleError', err, IS_STORE_ROOT);
    }
  },
  async updateViewColumnsSize({ dispatch, getters, commit }, { variables }) {
    try {
      const newColumnsSize = variables?.columnsSize ?? [];
      const {
        selectedView = {
        },
      } = getters ?? {
      };
      const { columnsSize = [] } = selectedView ?? {
      };
      const updatedColumnsSize = columnsSize.map(col => {
        const changedCol = newColumnsSize.find(newCol => newCol.id == col.id);
        if (changedCol) {
          return {
            ...col,
            size: changedCol.size,
          };
        }
        return col;
      });
      commit('ProjectDetailsTableSchedule/setHoveredColumn', {
        clicked: false,
        index: -1,
        active: false,
      }, IS_STORE_ROOT);
      dispatch('updateViewData', {
        id: variables.viewId,
        columnsSize: updatedColumnsSize,
      });
      ProjectViewsApi.updateViewColumnsSize(variables);
    } catch (err) {
      dispatch('handleError', err, IS_STORE_ROOT);
    }
  },
  updateViewData({ dispatch, state }, viewData) {
    const { id } = viewData;
    const { views } = state;

    const newViews = views.map(view => {
      return id !== view.id ? view : {
        ...view,
        ...viewData,
      };
    });
    dispatch('setViews', {
      arrViews: newViews,
      allowSetViewMode: false,
      updateFilterTag: false,
    });
  },
  updateLocalViewColOrder({ dispatch, state }, { viewData, updateFilterTag = true }) {
    const { id, filterTag, sortingField } = viewData;
    const { views } = state;

    const newViews = views.map(view => {
      if (id !== view.id) return view;
      const newFilterTag = filterTagFormat(filterTag);
      return {
        ...view,
        ...viewData,
        filterTag: newFilterTag,
        sortingData: sortHelper(view.id, sortingField || view.sortingField),
      };
    });
    dispatch('setViews', {
      arrViews: newViews,
      allowSetViewMode: false,
      updateFilterTag,
    });
  },
  async resetView({ commit, dispatch, state }, payload) {
    try {
      commit('spinner', true, IS_STORE_ROOT);
      const { data } = await ProjectViewsApi.resetView(payload);
      const newView = data.response;
      const { views } = state;
      const newViews = views.map(view => {
        if (newView.id !== view.id) return view;
        const newFilterTag = filterTagFormat(newView?.filterTag);
        return {
          ...newView,
          sortingData: view?.sortingData,
          filterTag: newFilterTag,
        };
      });
      dispatch('setViews', {
        arrViews: newViews,
        allowSetViewMode: false,
      });
    } catch (err) {
      dispatch('handleError', err, IS_STORE_ROOT);
    } finally {
      commit('spinner', false, IS_STORE_ROOT);
    }
  },
  setGalleryToAllScheduleViews({ getters, dispatch }) {
    const obj = getters.viewsIds.reduce((result, option) => {
      return {
        ...result,
        [option]: true,
      };
    }, {
    });
    dispatch('setViewMode', obj, IS_STORE_ROOT);
  },
};

// prefernce of row height
// height in db -> default heights mapping -> TALL
function computeHeight(getters) {
  const { selectedView } = getters;
  if (selectedView.rowHeight) return selectedView.rowHeight;

  return getters.selectedViewCriteria;
}
function isFilteredTagView(selectedView) {
  return Object.keys(selectedView?.filterTag).length;
}
async function updateTagTotal({ rootState, dispatch, selectedView }) {
  const sid = rootState.ProjectDetailsTableSchedule.scheduleId;
  const tagName = selectedView.filterTag[0];
  dispatch('ProjectAggregation/getTotalOfTag', {
    tableId: sid,
    tableType: 'schedule',
    tagName: tagName,
  }, IS_STORE_ROOT);
}
// BACKCOMPAT: back support for old filterTag format (earlier list of tags, now it's object)
function filterTagFormat(filterTag) {
  if (!filterTag) return {
  };

  if (typeof filterTag === 'string') {
    let newFilterTag = convertStringToArray(filterTag);
    return filterTagFormat(newFilterTag);
  }

  if (Array.isArray(filterTag)) {
    return {
      [productHeaders.TAG]: filterTag,
    };
  }

  return filterTag;
}